<template>
  <div>
    <content-section icon="seat_choice" title="Scelta del posto">
      <div>
        <seat-map-header
          :is-editing="isEditing"
          :current-segment-index="currentSegmentIndex"
          :total-segments="segments.length"
          :current-segment="segments[currentSegmentIndex]"
          :current-itinerary="currentItinerary"
          :current-passenger="sittingPassengers[currentPassengerIndex]"
          :is-passenger-holding-infant="isPassengerHoldingAnInfant"
          :locations="locations"
          @toggle-edit="changeSeatSelection"
        />
        <div v-if="isEditing" class="seat-map-editor">
          <div class="seat-map-content">
            <seat-map-mobile-nav
              :current-segment="currentSegment"
              :current-segment-id="currentSegmentId"
              :current-passenger-id="currentPassengerId"
              :sitting-passengers="sittingPassengers"
              :parsed-selections="parsedSelections"
              :set-current-passenger-id="setCurrentPassengerId"
            />
            <!-- Navigation between segments and passengers -->
            <div
              :ref="`seat-map-info:scroll:desktop`"
              class="d-flex flex-column seat-map-side-bar"
            >
              <div
                class="d-none flex-grow d-lg-block d-xl-block"
                v-if="showLegend['desktop']"
              >
                <seat-map-info
                  :list="seatsLegend"
                  :onHide="toggleShowLegendDesktop"
                />
              </div>
              <seat-map-navigation
                v-else
                :disabled="showLegend['mobile']"
                :segments="segments"
                :currentSegmentIndex="currentSegmentIndex"
                :currentSegmentId="currentSegmentId"
                :setCurrentSegmentId="setCurrentSegmentId"
                :availableDecks="availableDecks"
                :currentDeckIndex="String(currentDeckIndex)"
                :setCurrentDeckIndex="setCurrentDeckIndex"
                :sittingPassengers="sittingPassengers"
                :currentPassengerId="currentPassengerId"
                :setCurrentPassengerId="setCurrentPassengerId"
                :parsedSelections="parsedSelections"
                :resetSegmentSelections="resetSegmentSelections"
              />
              <seat-map-info-button
                :showOnMobile="false"
                :toggled="showLegend['desktop']"
                :toggle="toggleShowLegendDesktop"
              />
            </div>
            <!-- Seats configuration -->
            <div class="seat-map-main">
              <div class="overflow-hidden position-relative">
                <div class="seat-map-container content-panel position-relative">
                  <deck
                    v-if="isCurrentDeckEnabled"
                    :currentTravelerChoosing="currentPassengerId"
                    :deckConfiguration="deckConfiguration"
                    :mappedDeck="mappedDeck"
                    :selectedSeats="selectedSeats"
                    :handleSeatSelection="onDeckSeatSelected"
                  />
                  <deck-disabled-message v-else />
                </div>
                <div
                  :ref="`seat-map-info:scroll:mobile`"
                  class="infant-notice-container w-100 h-100"
                >
                  <div
                    v-if="showLegend['mobile']"
                    class="seat-map-info-container d-block position-absolute d-lg-none d-xl-none flex-grow w-100 h-100 pointer-events-all"
                  >
                    <seat-map-info
                      :list="seatsLegend"
                      :onHide="toggleShowLegendMobile"
                    />
                  </div>
                  <div
                    v-else-if="
                      isPassengerHoldingAnInfant && isCurrentDeckEnabled
                    "
                    class="d-none d-lg-flex d-xl-flex infant-notice pointer-events-all shadow-lg"
                    v-b-popover.hover.right="
                      'Stai selezionando un posto condiviso con un neonato.'
                    "
                  >
                    <vb-icon name="infant"></vb-icon>
                  </div>
                </div>
              </div>

              <div class="p-3">
                <seat-map-info-button
                  showOnMobile
                  :toggled="showLegend['mobile']"
                  :toggle="toggleShowLegendMobile"
                />
              </div>
              <seat-map-footer
                :mustSelectSeats="mustSelectSeats"
                :isPreviousActionVisible="segments.length > 1"
                :isPreviousActionDisabled="
                  currentSegmentIndex === 0 || showLegend['mobile']
                "
                :previousSegmentAction="previousSegment"
                :nextSegmentActionLabel="
                  isLastSegmentSelected ? 'Conferma' : 'Prossimo volo'
                "
                :isNextSegmentActionDisabled="isNextSegmentActionDisabled"
                :handleNextSegmentAction="handleNextSegmentAction"
                :parsedSelections="parsedSelections"
                :currencySymbol="currencySymbol"
              />
            </div>
          </div>
          <div
            v-if="!mustSelectSeats"
            class="seat-map-skip border-top pt-3"
            @click="skipSeatChoice"
            style="cursor: pointer"
          >
            <vb-radio
              :vb_options="{
                type: 'light',
              }"
            />
            <div>
              <div class="semibold">
                No grazie, non voglio scegliere il posto a sedere
              </div>
              <div>
                Se salti la selezione ora, i posti verranno assegnati dalla
                compagnia. La compagnia non garantisce che gli adulti siano
                seduti vicini. Alcune compagnie permettono di nuovo questa
                scelta al check-in online o in aeroporto.
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <seat-summary
            :itineraries="itineraries"
            :passengers="passengers"
            :selections="parsedSelections"
          />
          <vb-button
            v-if="!isEditing"
            class="d-flex ml-auto border mr-4"
            :vb_options="{
              variant: 'outline-info',
              size: 'md',
              role: 'button',
            }"
            @click="changeSeatSelection"
          >
            <span
              class="text-decoration-underline font-size-14px d-flex justify-content-center align-items-center"
            >
              <vb-icon
                name="pen"
                :color="sass('info')"
                :size="14"
                class="mr-2"
              />
              <span>Modifica</span>
            </span>
          </vb-button>
          <!-- Warning Notice -->
          <hr />
          <div class="seat-map-skip">
            <vb-icon name="info" :size="18"></vb-icon>
            <span class="font-size-16px">
              La compagnia aerea non garantisce l'attribuzione dei posti da te
              selezionati e si riserva il diritto di riassegnartene altri con
              caratteristiche analoghe senza alcun costo aggiuntivo.
            </span>
          </div>
        </div>
      </div>
    </content-section>
  </div>
</template>

<script>
import {
  facilityLegend,
  make_seats_legend,
} from "@liveflight/resources/seat-map";
import { is_mobile } from "@/utilities/commons";
import {
  filterHeldPassengers,
  sortPassengers,
  is_everyone_seated,
} from "@liveflight/utilities/seat-map";
import ContentSection from "@liveflight/components/blocks/wrappers/content-section";
import Deck from "@liveflight/components/blocks/seat-map/deck/deck.vue";
import Summary from "@liveflight/components/blocks/seat-map/summary/seat-summary.vue";
import SeatMapInfo from "@liveflight/components/blocks/seat-map/legend/seat-map-info.vue";
import SeatMapInfoButton from "@liveflight/components/blocks/seat-map/legend/seat-map-info-button.vue";
import SeatMapNavigation from "@liveflight/components/blocks/seat-map/navigation/seat-map-navigation.vue";
import SeatMapMobileNav from "@liveflight/components/blocks/seat-map/navigation/seat-map-mobile-nav.vue";
import SeatMapFooter from "@liveflight/components/blocks/seat-map/summary/seat-map-footer.vue";
import SeatMapHeader from "@liveflight/components/blocks/seat-map/header/seat-map-header.vue";
import DeckDisabledMessage from "@liveflight/components/blocks/seat-map/messages/deck-disabled-message.vue";
import { get_known_locations } from "@liveflight/providers";

export default {
  name: "seat-map",
  components: {
    deck: Deck,
    "content-section": ContentSection,
    "seat-map-info-button": SeatMapInfoButton,
    "seat-map-navigation": SeatMapNavigation,
    "seat-map-mobile-nav": SeatMapMobileNav,
    "seat-map-footer": SeatMapFooter,
    "seat-map-info": SeatMapInfo,
    "seat-summary": Summary,
    "seat-map-header": SeatMapHeader,
    "deck-disabled-message": DeckDisabledMessage,
  },
  props: {
    mustSelectSeats: Boolean,
    mappedDecks: Object,
    passengers: Array,
    selections: String,
    handleSeatSelection: Function,
    resetSeatSelection: Function,
    updateParentWithCurrentStatus: Function,
    resetSegmentSelections: Function,
  },
  data() {
    return {
      isEditing: true,
      currentSegmentId: "",
      currentPassengerId: "",
      currentDeckIndex: 0,
      showLegend: {
        mobile: false,
        desktop: false,
      },
      // Tracks the "was the current segment complete?" reading from the
      // last watcher tick. Vue 2 deep watchers reuse the same reference
      // for old/new, so we can't derive the transition from oldSelections.
      wasSegmentComplete: false,
    };
  },
  mounted() {
    this.selectFirstSegment(this.segments);
    if (this.availableDecks.length) {
      const firstAvailableDeck = this.availableDecks.findIndex(
        (deck) => !deck.disabled,
      );
      this.currentDeckIndex = Math.max(0, firstAvailableDeck);
    }
    const _this = this;
    window.eventBus.$on("seat_map_next_step", () => {
      _this.handleNextSegmentAction();
    });
    this.updateParentStatus(
      this.isEditing,
      this.isLastSegmentSelected,
      this.isNextSegmentActionDisabled,
    );
  },
  computed: {
    locations() {
      return get_known_locations(this.$store.state.liveflight.flight_offer) || {};
    },
    itineraries() {
      try {
        return this.$store.state.liveflight.flight_offer.itineraries;
      } catch {
        return [];
      }
    },
    currentItinerary() {
      const itineraryIndex = this.itineraries.findIndex((itinerary) =>
        itinerary.segments.some(
          (segment) => segment.id === this.currentSegmentId,
        ),
      );
      return this.itineraries[Math.max(0, itineraryIndex)];
    },
    currentSegmentIndex() {
      return this.segments.findIndex(
        (d) => d.segmentId === this.currentSegmentId,
      );
    },
    currentPassengerIndex() {
      return this.getPassengerIndex(this.currentPassengerId);
    },
    isLastSegmentSelected() {
      return this.currentSegmentIndex === this.segments.length - 1;
    },
    isPassengerHoldingAnInfant() {
      const { holding } = this.sittingPassengers[this.currentPassengerIndex] || {};
      return Boolean(holding);
    },
    currentSegment() {
      return this.segments[Math.max(0, this.currentSegmentIndex)];
    },
    availableDecks() {
      if (this.currentSegment && this.currentSegment.decks) {
        return this.currentSegment.decks.map((d, index) => ({
          id: index,
          deckType: d.deckType,
          value: index,
          label: d.deckType,
        }));
      }
      return [];
    },
    isCurrentDeckEnabled() {
      if (process && process.env) {
        return (
          !this.mappedDeck.disabled ||
          process.env.VUE_APP_SHOW_DISABLED_SEAT_MAP
        );
      }
      return !this.mappedDeck.disabled;
    },
    currentDeck() {
      return this.currentSegment.decks[this.currentDeckIndex];
    },
    deckConfiguration() {
      if (this.currentDeck) return this.currentDeck.deckConfiguration || {};
      return {};
    },
    mappedDeck() {
      const currentSegmentDecks = this.mappedDecks[this.currentSegmentId];
      if (!currentSegmentDecks) {
        return {};
      }
      return currentSegmentDecks.decks[this.currentDeckIndex]
        ? currentSegmentDecks.decks[this.currentDeckIndex]
        : {};
    },
    currencySymbol() {
      return this.mappedDeck.currencySymbol || "";
    },
    /**
     * Aggregate every deck's legendHints, then filter the static legend
     * to entries this seatmap actually surfaces. Facility entries are
     * built from `facility:CODE:DESC` hints and resolved through
     * facilityLegend (deduped on code so a lavatory shown on two
     * segments doesn't duplicate the legend row).
     */
    allLegendHints() {
      return Object.values(this.mappedDecks)
        .flatMap((s) => Object.values(s.decks || {}))
        .reduce((acc, d) => (d && d.legendHints ? acc.concat(d.legendHints) : acc), []);
    },
    seatsLegend() {
      const hints = this.allLegendHints;
      const filtered = make_seats_legend(this.currencySymbol).filter(
        (entry) => !entry.hint || entry.hint.split(",").some((h) => hints.indexOf(h) >= 0)
      );
      const seenFacilityCodes = {};
      const facilities = [];
      hints.forEach((hint) => {
        if (!hint.startsWith("facility:")) return;
        const [, code, description] = hint.split(":");
        if (!code || seenFacilityCodes[code]) return;
        seenFacilityCodes[code] = true;
        facilities.push({
          type: "facility",
          code,
          legend: facilityLegend[code] || description || code,
        });
      });
      return filtered.concat(facilities);
    },
    segments() {
      return Object.values(this.mappedDecks).map((obj) => obj.segment);
    },
    parsedSelections() {
      try {
        const selections = JSON.parse(this.selections);
        return selections;
      } catch (e) {
        this.log("parsedSeatSelections error");
        this.log(e);
        return {};
      }
    },
    selectedSeats() {
      const mappedSeats = {};

      if (!this.currentSegmentId) {
        return mappedSeats;
      }

      const passengerSeats = this.parsedSelections[this.currentSegmentId];
      if (!passengerSeats) {
        return mappedSeats;
      }
      this.passengers.forEach((p) => {
        const seat = passengerSeats[p.id];
        if (seat) {
          mappedSeats[seat.number] = p;
        }
      });
      return mappedSeats;
    },
    sittingPassengers() {
      const sittingPassengers = this.filterHeldPassengers(this.passengers);
      return this.sortPassengers(sittingPassengers);
    },
    isNextSegmentActionDisabled() {
      if (this.showLegend["mobile"]) {
        return true;
      }
      if (!this.mustSelectSeats || !this.isLastSegmentSelected) {
        return false;
      }
      const target = this.sittingPassengers.length;
      return this.segments.some(
        (s) => !is_everyone_seated(this.parsedSelections, s.segmentId, target),
      );
    },
  },
  created() {
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
    window.eventBus.$off("seat_map_next_step");
  },
  methods: {
    filterHeldPassengers,
    sortPassengers,
    onResize() {
      if (this.isEditing) {
        this.showLegend = {
          mobile: false,
          desktop: false,
        };
      }
    },
    selectFirstSegment(segments) {
      if (segments.length) {
        this.setCurrentSegmentId(segments[0].segmentId);
      }
    },
    changeSeatSelection() {
      this.$emit("tracked_event", {
        event: "seat-map:selection:change",
        data: {
          type: "info",
        },
      });
      this.selectFirstSegment(this.segments);
      this.isEditing = true;
    },
    getPassengerIndex(passengerId) {
      return this.sittingPassengers.findIndex((d) => d.id === passengerId);
    },
    setCurrentPassengerId(newPassengerId) {
      this.currentPassengerId = newPassengerId;
    },
    setCurrentDeckIndex(newDeckIndex) {
      this.currentDeckIndex = Math.min(
        this.availableDecks.length - 1,
        Math.max(0, newDeckIndex),
      );
    },
    setCurrentSegmentId(newSegmentId) {
      this.currentSegmentId = newSegmentId;
      this.setCurrentPassengerId(this.sittingPassengers[0].id);
      this.setCurrentDeckIndex(0);
      this.scroll_top();
    },
    /**
     * Avanza il pax corrente dopo una selezione di seat.
     *  1) Cerca un pax non ancora assegnato sul segmento corrente,
     *     partendo da currentPassengerIndex+1 con wrap-around. Copre
     *     flow lineare, re-edit asimmetrico, e swap di un seat.
     *  2) Fallback lineare con wrap se tutti i pax sono già assegnati,
     *     così l'utente continua a scorrere anche quando non manca
     *     nessuno; l'auto-advance del segmento (watcher su
     *     parsedSelections) gestisce la transizione finale.
     */
    nextPassenger() {
      const total = this.sittingPassengers.length;
      if (!total) return;
      const segSelections =
        (this.parsedSelections || {})[this.currentSegmentId] || {};
      for (let offset = 1; offset <= total; offset += 1) {
        const idx = (this.currentPassengerIndex + offset) % total;
        const candidate = this.sittingPassengers[idx];
        if (!segSelections[candidate.id]) {
          this.setCurrentPassengerId(candidate.id);
          return;
        }
      }
      // Fallback: nessun pax mancante, avanza linearmente con wrap.
      const nextIndex = (this.currentPassengerIndex + 1) % total;
      this.setCurrentPassengerId(this.sittingPassengers[nextIndex].id);
    },
    moveToSegment(direction) {
      this.$emit("tracked_event", {
        event: `seat-map:navigation:${direction > 0 ? "next-segment" : "previous-segment"}`,
        data: {
          type: "info",
        },
      });

      const nextIndex = this.currentSegmentIndex + direction;
      const nextSegment =
        this.segments[
          Math.min(Math.max(nextIndex, 0), this.segments.length - 1)
        ];
      this.setCurrentSegmentId(nextSegment.segmentId);
    },
    nextSegment() {
      this.moveToSegment(1);
    },
    previousSegment() {
      this.moveToSegment(-1);
    },
    handleNextSegmentAction() {
      // Sanity check since it could be triggered from parent
      if (this.isNextSegmentActionDisabled || !this.isEditing) {
        return;
      }

      if (this.isLastSegmentSelected) {
        this.$emit("tracked_event", {
          event: "seat-map:selection:confirm",
          data: {
            type: "info",
          },
        });
        this.isEditing = false;
        return;
      }

      this.nextSegment();
    },
    skipSeatChoice() {
      this.resetSeatSelection();
      this.$emit("tracked_event", {
        event: "seat-map:selection:skip",
        data: {
          type: "info",
        },
      });
      this.isEditing = false;
    },
    toggleShowLegend(device) {
      this.showLegend = {
        ...Object.fromEntries(
          Object.entries(this.showLegend).map((entry) => {
            const key = entry[0];
            return [key, key === device ? !entry[1] : false];
          }),
        ),
      };
      if (this.showLegend[device] && this.$refs) {
        this.$emit("tracked_event", {
          event: "seat-map:show-legend",
          data: {
            type: "info",
            device,
          },
        });
        const legend_wrapper_ref = this.$refs[`seat-map-info:scroll:${device}`];
        const ref = Array.isArray(legend_wrapper_ref)
          ? legend_wrapper_ref[0]
          : legend_wrapper_ref;
        if (ref && ref.scrollIntoView) {
          ref.scrollIntoView({
            behavior: "smooth",
          });
        }
      }
    },
    updateParentStatus(isEditing, isLastSegmentSelected, btnDisabled) {
      let btnText = "Continua";
      if (isEditing) {
        btnText = isLastSegmentSelected ? "Conferma" : "Prossimo volo";
      }
      this.updateParentWithCurrentStatus({ isEditing, btnText, btnDisabled });
    },
    toggleShowLegendDesktop() {
      this.toggleShowLegend("desktop");
    },
    toggleShowLegendMobile() {
      this.toggleShowLegend("mobile");
    },
    /**
     * handleSeatSelection torna { success, displacedOwnerId? }: il host
     * distingue assignment da de-select; sul de-select, riporta il pax
     * appena rimosso così posizioniamo il cursore giusto.
     */
    onDeckSeatSelected(seat) {
      const response = this.handleSeatSelection(
        this.currentSegmentId,
        this.currentPassengerId,
        seat,
      );
      if (!response || !response.success) return;
      if (response.displacedOwnerId) {
        // DE-SELECT: parcheggia il cursore sul pax appena rimosso.
        this.setCurrentPassengerId(response.displacedOwnerId);
      } else {
        // ASSIGNMENT: avanza al prossimo pax (first-unassigned con wrap).
        this.nextPassenger();
      }
    },
  },
  watch: {
    isEditing: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        this.$nextTick(() => {
          _this.scroll_top();
          _this.updateParentStatus(
            newValue,
            _this.isLastSegmentSelected,
            _this.isNextSegmentActionDisabled,
          );
        });
      },
    },
    isLastSegmentSelected: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        if (this.isEditing) {
          this.$nextTick(() => {
            _this.updateParentStatus(
              _this.isEditing,
              newValue,
              _this.isNextSegmentActionDisabled,
            );
          });
        }
      },
    },
    isNextSegmentActionDisabled: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        this.$nextTick(() => {
          _this.updateParentStatus(
            _this.isEditing,
            _this.isLastSegmentSelected,
            newValue,
          );
        });
      },
    },
    /**
     * Auto-advance del segmento quando tutti i pax del segmento
     * corrente hanno un seat. Mobile-only. Fire SOLO sulla transizione
     * incomplete → complete, così edit ripetuti su un segmento già
     * pieno non riavviano l'advance. $nextTick per disaccoppiare dai
     * watcher che reagiscono allo state innescato da handleNextSegmentAction.
     */
    parsedSelections: {
      deep: true,
      handler(newSelections) {
        if (!is_mobile()) return;
        const seg = this.currentSegmentId;
        const target = this.sittingPassengers.length;
        if (!seg || !target) return;
        const isComplete = is_everyone_seated(newSelections, seg, target);
        const wasComplete = this.wasSegmentComplete;
        this.wasSegmentComplete = isComplete;
        if (!wasComplete && isComplete) {
          this.$nextTick(() => this.handleNextSegmentAction());
        }
      },
    },
    // Reset the completeness snapshot when the user moves to a new
    // segment — otherwise a previously-complete segment leaves the
    // snapshot true and the next segment's incomplete→complete
    // transition is silently swallowed.
    currentSegmentId() {
      this.wasSegmentComplete = is_everyone_seated(
        this.parsedSelections,
        this.currentSegmentId,
        this.sittingPassengers.length,
      );
    },
  },
};
</script>

<style lang="scss">
.flex-grow {
  flex: 1;
}

.seat-map-legend {
  text-align: right;
}

.gap-16 {
  gap: 16px;
}

.seat-map-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.seat-map-header {
  gap: 0.5em;
  @include media-breakpoint-up(xl) {
    padding: 1em 0;
  }
}

.seat-map-side-bar {
  flex-basis: 30%;
}

.seat-map-main {
  flex-basis: 70%;
}

.seat-map-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-around;

  min-height: 50em;
  max-height: 50em;

  overflow-y: scroll;
  overflow-x: scroll;

  padding: 0 !important;
  background-color: $light-gray-blue !important;
}

.seat-map-skip {
  display: flex;
  flex-direction: row;
  align-items: baseline;

  justify-content: center;
  gap: 1em;
}
.carrier-logo {
  max-height: 30px;
}

.segment-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.text-decoration-underline {
  text-decoration: underline;
}

.infant-notice {
  pointer-events: all;
  position: absolute;
  bottom: 40px;
  left: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  max-width: 40px;
  min-width: 40px;

  max-height: 40px;
  min-height: 40px;
  background-color: white;
}

.infant-notice-container {
  position: absolute;
  top: 0;
  pointer-events: none;
}

.pointer-events-all {
  pointer-events: all;
}

.seat-map-info-container {
  background-color: white;
}

.seat-map-content {
  display: flex;
  flex-direction: column;
  gap: $grid-gutter-width/2;
  @include media-breakpoint-up(xl) {
    flex-direction: row;
  }
}
</style>
